import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import TableComponent from "components/common/TableComponent";
import CustomDatePicker from "components/common/DatePicker";
import Select from "components/common/Select";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import customerPaymentSummaryColumns from "components/Screens/Customers/CustomerDetials/PaymentHistoryColumns/PaymentHistoryColumns";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";
import usePaginationReducer from "hooks/usePaginationReducer";

import { downloadCustomerInvoicePDF, getCustomerPaymentHistory, paymentHistoryRetryPayment } from "services/customer";
import { GetCustomerPaymentById } from "store/actions/Customers/GetCustomerPaymentById";
import { formatToYearMonth, stringToKebabCase } from "helpers";
import { sendCustomerInvoiceEmail } from "services/Admin/Customer";
import { usePermissions } from "components/Hook";
import { hasPermission } from "hooks/usePermission";

import {
  PAYMENT_HISTORY_SORT_FIELD_MAP,
  PAYMENT_HISTORY_TABLE_ROWS_PER_PAGE_OPTIONS,
  PAYMENT_STATUS_DROPDOWN_OPTIONS,
  PAYMENT_TYPE_DROPDOWN_OPTIONS,
} from "components/Screens/Customers/CustomerDetials/constants";
import { CUSTOMER_PAYROLL_DETAILS_ROUTE } from "constants/routes";
import { USER_TYPES } from "constants/index";

import "./PaymentHistoryRevamped.scss";

function PaymentHistoryTableRevamped({ source = "", customerId, getSelectedPaymentId = () => {} }) {
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [orderBy, setOrderBy] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: AlertType.error });
  const dispatch = useDispatch();

  const navigateTo = useNavigate();
  const { loggedInUserType } = usePermissions();

  const canRetry = hasPermission("payments.retry");
  const canSendInvoice = hasPermission("payments.send_invoice");

  const [{ page, rowsPerPage, totalCount }, paginationDispatch] = usePaginationReducer();

  const fetchCustomerPaymentSummary = async ({ limit, currentPage, appliedFilters }) => {
    setLoading(true);

    const queryParams = new URLSearchParams({
      limit,
      page: currentPage,
      filter: JSON.stringify(appliedFilters),
      ...(orderBy && Object.keys(orderBy).length > 0 ? { orderBy: JSON.stringify(orderBy) } : {}),
    }).toString();

    try {
      const response = await getCustomerPaymentHistory(customerId, queryParams);
      const { rows = [], count = 0 } = response || {};
      setRowsData(rows);
      paginationDispatch({ type: "SET_TOTAL_COUNT", payload: count });
    } catch (error) {
      const errorMessage = error[0]?.description || "Something went wrong, please try again";

      setSnackbar({
        open: true,
        message: errorMessage,
        type: AlertType.error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchCustomerPaymentSummary({
        currentPage: page,
        limit: rowsPerPage,
        appliedFilters: filters,
        orderBy,
      });
    }
  }, [customerId, rowsPerPage, page, filters, orderBy]);

  const handleFilterChange = (name, value) => {
    if (value === null) return;
    let formattedValue = value;

    if (name === "billingPeriod" && value) {
      formattedValue = formatToYearMonth(value);
    }

    setFilters((prev) => ({ ...prev, [name]: formattedValue }));
    paginationDispatch({ type: "SET_PAGE", payload: 0 });
  };

  const handleClearAll = () => {
    setFilters({});
    paginationDispatch({ type: "RESET" });
  };

  const handleSort = (property, direction) => {
    if (PAYMENT_HISTORY_SORT_FIELD_MAP[property]) {
      setOrderBy({ field: PAYMENT_HISTORY_SORT_FIELD_MAP[property], direction });
    }
  };

  const handleRetryPayment = async (row) => {
    setLoading(true);
    const { id: paymentId, paymentType } = row;
    const normalizeString = stringToKebabCase(paymentType);
    try {
      const response = await paymentHistoryRetryPayment(customerId, paymentId, normalizeString);
      if (response?.ok) {
        setSnackbar({
          open: true,
          message: "Credit updated successfully",
          type: AlertType.success,
        });
        await fetchCustomerPaymentSummary({
          limit: rowsPerPage,
          currentPage: page,
          appliedFilters: filters,
        });
      }
    } catch (error) {
      const errorMessage = error[0]?.description || "Something went wrong, please try again";
      setSnackbar({
        open: true,
        message: errorMessage,
        type: AlertType.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayrollViewClick = (row) => {
    const { id, paymentType } = row;
    navigateTo(CUSTOMER_PAYROLL_DETAILS_ROUTE.replace(":payrollId", id), {
      state: {
        customerId,
        paymentType,
      },
    });
  };
  const handleAdminPayrollViewClick = (row) => {
    const { id, billingPeriod, paymentType, total, status } = row;

    const paymentHistoryData = {
      billingPeriod,
      paymentType,
      total,
      status,
    };

    getSelectedPaymentId(id, paymentHistoryData);
    dispatch(
      GetCustomerPaymentById({
        request: `customerPayment/${id}`,
        rowsPerPage: 10,
        page: 0,
        customerId,
      })
    );
  };

  const handlePayrollDownloadClick = async (row) => {
    const { id, paymentType } = row;
    await downloadCustomerInvoicePDF(id, paymentType?.toLowerCase(), dispatch, customerId);
  };

  const handleSendInvoiceEmail = async (row) => {
    try {
      setLoading(true);
      const { id, paymentType } = row;
      await sendCustomerInvoiceEmail(paymentType?.toLowerCase(), id, customerId);
      setSnackbar({
        open: true,
        message: "Invoice email sent successfully",
        type: AlertType.success,
      });
    } catch (error) {
      const errorMessage = error[0]?.description || "Something went wrong, please try again";
      setSnackbar({
        open: true,
        message: errorMessage,
        type: AlertType.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableActionClick = (action, row) => {
    const { label } = action;

    if (label === "Download") {
      handlePayrollDownloadClick(row);
      return;
    }

    if (label === "SendInvoiceEmail") {
      handleSendInvoiceEmail(row);
      return;
    }

    if (label === "View") {
      if (source === "customer") {
        handlePayrollViewClick(row);
      } else {
        handleAdminPayrollViewClick(row);
      }
    }
  };

  const columns = customerPaymentSummaryColumns(
    {
      onRetry: handleRetryPayment,
      showSendInvoiceEmail: loggedInUserType === USER_TYPES.EDGE && canSendInvoice,
      showRetry: canRetry,
    },
    source
  );
  const { billingPeriod, paymentType, status } = filters;

  return (
    <div className="payment-history-table">
      {loading && <PageLoader />}

      <div className="filter-row">
        <div className="filter-field payment-history-date-picker">
          <CustomDatePicker
            onChange={(date) => handleFilterChange("billingPeriod", date)}
            label="Billing period"
            monthYearOnly
            value={billingPeriod}
            placeholder="Select"
          />
        </div>

        <div className="filter-field">
          <Select
            label="Payment type"
            options={PAYMENT_TYPE_DROPDOWN_OPTIONS}
            name="paymentType"
            placeholder="Select"
            labelClass="payment-history-dropdown"
            value={paymentType}
            onChange={(value) => handleFilterChange("paymentType", value)}
          />
        </div>

        <div className="filter-field">
          <Select
            label="Status"
            options={PAYMENT_STATUS_DROPDOWN_OPTIONS}
            name="status"
            placeholder="Select"
            labelClass="payment-history-dropdown"
            value={status}
            onChange={(value) => handleFilterChange("status", value)}
          />
        </div>

        {(billingPeriod || paymentType || status) && (
          <div className="clear-all-button">
            <Button
              label="Clear All"
              size="small"
              color="secondary"
              disableHoverEffect
              variant="text"
              onClick={handleClearAll}
            />
          </div>
        )}
      </div>

      <TableComponent
        columns={columns}
        data={rowsData}
        rowsPerPage={rowsPerPage}
        page={page}
        totalCount={totalCount}
        onPageChange={(newPage) => paginationDispatch({ type: "SET_PAGE", payload: newPage })}
        onRowsPerPageChange={(newRowsPerPage) =>
          paginationDispatch({ type: "SET_ROWS_PER_PAGE", payload: newRowsPerPage })
        }
        tableHeadSmall
        tableCellSmall
        rowsPerPageOptions={PAYMENT_HISTORY_TABLE_ROWS_PER_PAGE_OPTIONS}
        hasVerticalBorder
        onSort={handleSort}
        onAction={handleTableActionClick}
      />
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={1500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
}

export default PaymentHistoryTableRevamped;
